import { FC, useContext, useMemo } from 'react'
import { useQuery } from 'react-query'
import {
  createResponseContext,
  initialQueryResponse,
  Permission,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../../../_metronic/helpers'
import { getFormularios, getFormulariosByEmpresa } from './_requests'
import { useQueryRequest } from './QueryRequestProvider'
import { FormularioList } from './_models'
import { useModal } from '../../../../../components'
import { useAuth } from '../../../../auth'

const QueryResponseContext = createResponseContext<FormularioList>(initialQueryResponse)

export const QueryResponseProvider: FC<WithChildren> = ({ children }) => {
  const { isOpen } = useModal()
  const { state } = useQueryRequest()
  const { currentUser } = useAuth()

  const query = useMemo(() => stringifyRequestQuery(state), [state])

  const isSuperAdmin = currentUser?.grupo?.nome === Permission.SuperAdmin
  const canFetch = isSuperAdmin || (state.filters && state.filters?.length > 0)

  const {
    isFetching,
    isError,
    refetch,
    data: response,
  } = useQuery(
    [QUERIES.FORMULARIOS_LIST, query, isOpen, isSuperAdmin],
    async () => {
      if (!canFetch) return

      return isSuperAdmin ? getFormularios(query) : getFormulariosByEmpresa(query)
    },
    {
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !isOpen && canFetch,
    }
  )

  return (
    <QueryResponseContext.Provider
      value={{
        isLoading: isFetching,
        isError,
        refetch,
        response,
        query,
      }}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

export const useQueryResponse = () => useContext(QueryResponseContext)
